import { formatStockMoney, stockMovementValue } from "../../../utility/stockValuation";
import "./StockOverview.scss";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import FormField from "../../../components/formField/FormField";
import Table from "../../../components/table/Table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import RestockModal from "./modals/RestockModal";
import InventoryModal from "./modals/InventoryModal";
import MovementsModal from "./modals/MovementsModal";
import AssociatedProductsModal from "./modals/AssociatedProductsModal";
import CreateStockModal from "./modals/CreateStockModal";
import CreateStockCategoryModal from "./modals/CreateStockCategoryModal";
import ImportExportStockModal from "./modals/ImportExportStockModal";
import DeleteStockCategoryModal from "./modals/DeleteStockCategoryModal";
import DeleteStockItemsModal from "./modals/DeleteStockItemsModal";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../firebase";
import getIcon from "../../../utility/getIcon";
import { ReactComponent as All } from "../../../images/All.svg";
import InventoryIcon from "../../../images/inventory.png";
import CombinedValueIcon from "../../../images/combinedValue.png";
import editIcon from "../../../images/EditIcon.svg";
import deleteIcon from "../../../images/DeleteIcon.svg";
import { getStockCategories, getStockItems, getStockLossStats } from "../../../utility/getStock";
import { normalizeStockLinks } from "../../../utility/stockManagement";
import { formatQuantity } from "../../../utility/inputValidation";
import { calculateStockLossSummary, getStockLossTone } from "../../../utility/stockLossStats";
import SkeletonLoader from "../../../components/skeletonLoader/SkeletonLoader";
dayjs.extend(relativeTime);

const PAGE_SIZE = 20;

const StatCard = ({ label, value, icon, iconLabel, tone = "default" }) => {
  return (
    <div className={`stat stat-${tone}`}>
      {icon ? (
        <img src={icon} className="stat-img" alt="" />
      ) : (
        <span className="stat-img stat-img-text">{iconLabel}</span>
      )}
      <div className="stat-details">
        <p className="stat-details-value">{value}</p>
        <p className="stat-details-name">{label}</p>
      </div>
    </div>
  );
};

function StockOverview({ org }) {
  const { t } = useTranslation("stockManagement");
  const alert = useAlert();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [searchText, setSearchText] = useState("");
  const [showButtonsId, setShowButtonsId] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [activeModal, setActiveModal] = useState({ key: null, row: null });
  const [page, setPage] = useState(1);
  const [stockItems, setStockItems] = useState([]);
  const [stockCategories, setStockCategories] = useState([]);
  const [stockLossStats, setStockLossStats] = useState([]);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStockData = useCallback(
    async (signal) => {
      setIsLoading(true);

      try {
        const productsQuery = query(collection(db, "products"), where("uid", "==", org?.uid));
        const optionProductsQuery = query(
          collection(db, "OptionProducts"),
          where("uid", "==", org?.uid),
        );
        const [items, categories, lossStats, productsSnapshot, optionProductsSnapshot] =
          await Promise.all([
            getStockItems(org?.uid),
            getStockCategories(org?.uid),
            getStockLossStats(org?.uid),
            getDocs(productsQuery),
            getDocs(optionProductsQuery),
          ]);

        if (signal?.aborted) return;
        setStockItems(items);
        setStockCategories(categories);
        setStockLossStats(lossStats);
        setProducts(
          [
            ...productsSnapshot.docs.map((doc) => doc.data()),
            ...optionProductsSnapshot.docs.map((doc) => doc.data()),
          ].filter((product) => normalizeStockLinks(product).length > 0),
        );
      } catch (error) {
        if (signal?.aborted) return;
        console.log("Error while fetching stock data: " + error);
        alert.show(t("stock_overview_load_error"), { type: "error" });
        setProducts([]);
        setStockItems([]);
        setStockCategories([]);
        setStockLossStats([]);
      } finally {
        if (signal?.aborted) return;
        setIsLoading(false);
      }
    },
    [org?.uid, alert, t],
  );

  useEffect(() => {
    const abortController = new AbortController();
    if (org?.uid) fetchStockData(abortController.signal);
    return () => abortController.abort();
  }, [org?.uid, fetchStockData]);

  const statusOptions = [
    { value: "All", label: t("stock_overview_status_option_all") },
    { value: "ok", label: t("stock_overview_status_option_ok") },
    { value: "low", label: t("stock_overview_status_option_low") },
    { value: "out", label: t("stock_overview_status_option_out") },
  ];
  const actionOptions = [
    { key: "editStockProduct", label: t("stock_overview_action_edit") },
    { key: "restock", label: t("stock_overview_action_restock") },
    { key: "inventoryCount", label: t("stock_overview_action_inventory_count") },
    { key: "viewMovements", label: t("stock_overview_action_view_movements") },
    { key: "usedInProducts", label: t("stock_overview_action_used_in_products") },
    { key: "deleteStockProduct", label: t("stock_overview_action_delete") },
  ];

  const getStatusLabel = (status) => {
    if (status === "ok") return t("stock_overview_status_badge_ok");
    if (status === "low") return t("stock_overview_status_badge_low");
    return t("stock_overview_status_badge_out");
  };
  const getStatus = useCallback((item) => {
    if (item.onHand <= 0) return "out";
    if (item.onHand <= item.minAlert) return "low";
    return "ok";
  }, []);

  const goToPage = (nextPage) => {
    setSelectedItemIds([]);
    setPage(Math.max(1, Math.min(nextPage, totalPages)));
  };

  const filteredItems = useMemo(() => {
    let items = stockItems;
    if (selectedCategory !== "All") {
      items = items.filter((item) => item.categoryId === selectedCategory.id);
    }
    if (selectedStatus !== "All") {
      items = items.filter((item) => getStatus(item) === selectedStatus);
    }
    if (searchText) {
      const textFormatted = searchText.toLowerCase();
      items = items.filter(
        (item) =>
          item.id.toLowerCase().includes(textFormatted) ||
          item.name.toLowerCase().includes(textFormatted),
      );
    }
    return items;
  }, [selectedCategory, selectedStatus, searchText, stockItems, getStatus]);

  const maxStockValue = useMemo(() => {
    if (!stockItems?.length || !products?.length) return 0;

    return stockItems.reduce((total, stockItem) => {
      const relatedProducts = products.filter((product) =>
        normalizeStockLinks(product).some((link) => link.stockItemId === stockItem.id),
      );
      if (!relatedProducts.length) return total;

      const maxValuePerUnit = relatedProducts.reduce((max, product) => {
        const matchingLink = normalizeStockLinks(product).find(
          (link) => link.stockItemId === stockItem.id,
        );
        return matchingLink?.quantityPerSale > 0
          ? Math.max(max, product?.price / matchingLink.quantityPerSale)
          : max;
      }, 0);

      return total + maxValuePerUnit * Math.max(Number(stockItem?.onHand) || 0, 0);
    }, 0);
  }, [stockItems, products]);

  const totalPaidPrice = useMemo(() => {
    if (stockItems.some(item => Number(item.onHand) !== 0 && item.unitCost == null)) return null;
    return stockItems.reduce((sum, item) => sum + Number(item.onHand || 0) * Number(item.unitCost || 0), 0);
  }, [stockItems]);

  const stockHealthStats = useMemo(() => {
    const counts = stockItems.reduce(
      (acc, item) => {
        acc[getStatus(item)] += 1;
        return acc;
      },
      { ok: 0, low: 0, out: 0 },
    );

    return {
      total: stockItems.length,
      low: counts.low,
      out: counts.out,
    };
  }, [stockItems, getStatus]);

  const lossStatsByItem = useMemo(
    () =>
      Object.fromEntries(
        stockLossStats
          .filter((stats) => stats?.stockItemId)
          .map((stats) => [stats.stockItemId, stats]),
      ),
    [stockLossStats],
  );

  const stockLossSummary = useMemo(
    () => calculateStockLossSummary(lossStatsByItem),
    [lossStatsByItem],
  );

  const stockLossSummaryTone = getStockLossTone(stockLossSummary.lossRate);
  const stockLossSummaryRateLabel = Number.isFinite(stockLossSummary.lossRate)
    ? `${stockLossSummary.lossRate.toFixed(1)}%`
    : "-";

  const totalPaidPriceLabel = Number.isFinite(totalPaidPrice) ? totalPaidPrice.toFixed(2) : " - ";
  const maxStockValueLabel = Number.isFinite(maxStockValue) ? maxStockValue.toFixed(2) : " - ";
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pagedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pagedItemIds = pagedItems.map((item) => item.id);

  const changeStatusHandler = (value) => {
    setSelectedStatus(value);
    setPage(1);
  };

  const changeCategoryHandler = (value) => {
    setSelectedCategory(value);
    setPage(1);
  };

  const openActionModal = (key, row) => {
    setActiveModal({ key, row });
    setShowButtonsId(null);
  };

  const closeActionModal = () => setActiveModal({ key: null, row: null });

  const closeAndRefetchStockData = async () => {
    closeActionModal();
    setPage(1);
    setSelectedItemIds([]);
    setSelectedCategory("All");
    setSelectedStatus("All");
    setSearchText("");
    await fetchStockData();
  };

  const stockItemIdsToDelete =
    activeModal.key === "deleteStockProduct" && activeModal?.row?.id
      ? [activeModal.row.id]
      : selectedItemIds;

  const toggleSelectAllPaged = (checked) => {
    setSelectedItemIds(checked ? [...pagedItemIds] : []);
  };

  const toggleSelectOne = (id, checked) => {
    setSelectedItemIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((selectedId) => selectedId !== id);
    });
  };

  const renderTable = () => (
    <Table
      columns={[
        {
          key: "select",
          label: (
            <input
              type="checkbox"
              checked={pagedItemIds.length > 0 && selectedItemIds.length === pagedItemIds.length}
              disabled={isLoading}
              onChange={(event) => toggleSelectAllPaged(event.target.checked)}
            />
          ),
          render: (_, row) => (
            <input
              type="checkbox"
              checked={selectedItemIds.includes(row.id)}
              disabled={isLoading}
              onChange={(event) => toggleSelectOne(row.id, event.target.checked)}
            />
          ),
        },
        {
          key: "name",
          label: t("stock_overview_column_stock_item"),
          render: (_, row) => (
            <div className="stock-item-cell">
              <p>{row.name}</p>
              <span>{row.id}</span>
            </div>
          ),
        },
        {
          key: "category",
          label: t("stock_overview_column_category"),
          render: (_, row) => {
            const category = stockCategories.find((category) => category.id === row.categoryId);
            if (!category) return <p>-</p>;
            return (
              <div className="category-cell">
                <img src={getIcon(category.name, category.customIcon)} alt={category.name} />
                <p>{category.name}</p>
              </div>
            );
          },
        },
        { key: "unit", label: t("stock_overview_column_unit") },
        { key: "unitCost", label: t("stock_unit_cost"), render: (_, row) => formatStockMoney(row.unitCost) },
        { key: "inventoryValue", label: t("stock_inventory_value"), render: (_, row) => formatStockMoney(stockMovementValue(row, row.onHand).value) },
        {
          key: "onHand",
          label: t("stock_overview_column_on_hand"),
          render: (_, row) => formatQuantity(row.onHand),
        },
        {
          key: "soldSinceLastInventory",
          label: t("stock_overview_column_sold_since_last_inventory"),
          render: (_, row) => formatQuantity(lossStatsByItem[row.id]?.soldQuantity),
        },
        {
          key: "lossRate",
          label: t("stock_overview_column_loss_rate"),
          render: (_, row) => {
            const lossStats = lossStatsByItem[row.id];
            if (!lossStats?.hasInventoryCount || !Number.isFinite(lossStats?.lossRate)) {
              return <span className="loss-rate-empty">-</span>;
            }

            const tone = getStockLossTone(lossStats.lossRate);
            return (
              <div className="loss-rate-cell">
                <span className={`loss-rate-badge loss-rate-badge-${tone}`}>
                  {lossStats.lossRate.toFixed(1)}%
                </span>
                <span>{formatQuantity(lossStats.lossQuantity)}</span>
              </div>
            );
          },
        },
        { key: "minAlert", label: t("stock_overview_column_min_alert") },
        {
          key: "status",
          label: t("stock_overview_column_status"),
          render: (_, row) => {
            const status = getStatus(row);
            return <span className={`status-badge ${status}`}>{getStatusLabel(status)}</span>;
          },
        },
        {
          key: "updatedAt",
          label: t("stock_overview_column_updated"),
          render: (_, row) => dayjs(row.updatedAt).fromNow(),
        },
        {
          key: "actions",
          label: t("stock_overview_column_actions"),
          render: (_, row) => (
            <div className="action-container">
              <button
                className="action-button"
                aria-label={t("stock_overview_column_actions")}
                disabled={isLoading}
                onClick={() => setShowButtonsId((prev) => (prev === row.id ? null : row.id))}
              >
                ⋮
              </button>
              {row.id === showButtonsId && (
                <div className="action-buttons">
                  {actionOptions.map((actionOption) => (
                    <button
                      key={actionOption.key}
                      disabled={isLoading}
                      onClick={() => openActionModal(actionOption.key, row)}
                    >
                      {actionOption.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ),
        },
      ]}
      data={pagedItems}
      isLoading={isLoading}
    />
  );

  return (
    <div className="stock-overview">
      <div className="stock-overview-header">
        <div className="stock-overview-header-copy">
          <div className="stock-overview-header-title">{t("stock_overview_title")}</div>
          <div className="stock-overview-header-subtitle">{t("stock_overview_subtitle")}</div>
        </div>

        <div className="stock-overview-header-stats">
          <div className="stock-overview-header-stats-inner">
            {isLoading ? (
              <>
                <SkeletonLoader className="stat-skeleton" />
                <SkeletonLoader className="stat-skeleton" />
                <SkeletonLoader className="stat-skeleton" />
                <SkeletonLoader className="stat-skeleton stat-skeleton-small" />
                <SkeletonLoader className="stat-skeleton stat-skeleton-small" />
                <SkeletonLoader className="stat-skeleton stat-skeleton-small" />
              </>
            ) : (
              <>
                <StatCard
                  label={t("stock_inventory_value")}
                  value={`${totalPaidPriceLabel}€`}
                  icon={InventoryIcon}
                  tone="money"
                />
                <StatCard
                  label={t("stock_overview_stat_potential_revenue_label")}
                  value={`${maxStockValueLabel}€`}
                  icon={CombinedValueIcon}
                  tone="money"
                />
                <StatCard
                  label={t("stock_overview_stat_loss_rate_label", {
                    count: stockLossSummary.itemsWithLoss,
                  })}
                  value={stockLossSummaryRateLabel}
                  iconLabel="%"
                  tone={stockLossSummaryTone}
                />
                <StatCard
                  label={t("stock_overview_stat_items_label")}
                  value={stockHealthStats.total}
                  iconLabel="#"
                />
                <StatCard
                  label={t("stock_overview_stat_low_label")}
                  value={stockHealthStats.low}
                  iconLabel="!"
                  tone="warning"
                />
                <StatCard
                  label={t("stock_overview_stat_out_label")}
                  value={stockHealthStats.out}
                  iconLabel="0"
                  tone="danger"
                />
              </>
            )}
          </div>
        </div>
      </div>
      <div className="stock-overview-body">
        <div className="stock-overview-body-filters">
          <div className="stock-overview-body-heading">
            <div>
              <p className="stock-overview-body-heading-title">
                {t("stock_overview_inventory_title")}
              </p>
              <p className="stock-overview-body-heading-subtitle">
                {t("stock_overview_inventory_count", {
                  count: filteredItems.length,
                  total: stockItems.length,
                })}
              </p>
            </div>
          </div>
          <div className="stock-overview-body-filters-row">
            <div className="stock-overview-body-filters-row-fields">
              <FormField
                type="text"
                label={t("stock_overview_search_label")}
                placeholder={t("stock_overview_search_placeholder")}
                value={searchText}
                onChange={setSearchText}
              />
              <FormField
                type="dropdown"
                label={t("stock_overview_status_label")}
                value={selectedStatus}
                dropDownValues={statusOptions}
                onChange={changeStatusHandler}
              />
            </div>

            <div className="stock-overview-body-filters-row-categories">
              {isLoading ? (
                <>
                  <SkeletonLoader className="stock-category-skeleton stock-category-skeleton-all" />
                  <SkeletonLoader className="stock-category-skeleton" />
                  <SkeletonLoader className="stock-category-skeleton" />
                  <SkeletonLoader className="stock-category-skeleton" />
                </>
              ) : (
                <>
                  <button
                    className={`category-btn ${selectedCategory === "All" ? "active" : ""}`}
                    disabled={isLoading}
                    onClick={() => changeCategoryHandler("All")}
                  >
                    <All />
                  </button>

                  {stockCategories.map((category) => (
                    <div
                      key={category.id}
                      className="stock-overview-body-filters-row-categories-custom"
                    >
                      <button
                        className={`category-btn ${
                          selectedCategory?.id === category.id ? "active" : ""
                        }`}
                        disabled={isLoading}
                        onClick={() => changeCategoryHandler(category)}
                      >
                        <img src={getIcon(category.name, category.customIcon)} alt="" />
                        <p>{category.name}</p>
                      </button>
                      <button
                        className="edit-btn"
                        disabled={isLoading}
                        onClick={() => openActionModal("editStockCategory", category)}
                      >
                        <img src={editIcon} alt="edit" />
                      </button>
                      <button
                        className="delete-btn"
                        disabled={isLoading}
                        onClick={() => openActionModal("deleteStockCategory", category)}
                      >
                        <img src={deleteIcon} alt="delete" />
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="stock-overview-body-filters-actions">
            <button disabled={isLoading} onClick={() => openActionModal("importExportStock", null)}>
              {t("stock_overview_import_export")}
            </button>
            <button
              disabled={isLoading || !selectedItemIds.length}
              onClick={() => setActiveModal({ key: "deleteStockItems", row: null })}
              className="delete-button"
            >
              {t("stock_overview_bulk_delete", { count: selectedItemIds.length })}
            </button>
            <button
              disabled={isLoading}
              onClick={() => openActionModal("createStockCategory", null)}
              className="category-button"
            >
              {t("stock_overview_add_category")}
            </button>
            <button
              disabled={isLoading}
              onClick={() => openActionModal("createStockProduct", null)}
              className="product-button"
            >
              {t("stock_overview_add_product")}
            </button>
          </div>
        </div>

        {!isLoading && pagedItems.length === 0 ? (
          <div className="stock-overview-empty">{t("stock_overview_table_empty")}</div>
        ) : (
          renderTable()
        )}
      </div>
      <div className="stock-overview-footer">
        <div className="pagination-actions">
          <button
            className="pagination-actions-button"
            onClick={() => goToPage(page - 1)}
            disabled={isLoading || page === 1}
          >
            {t("stock_overview_pagination_previous")}
          </button>
          <div className="pagination-actions-pages">
            {page} / {totalPages}
          </div>
          <button
            className="pagination-actions-button"
            onClick={() => goToPage(page + 1)}
            disabled={isLoading || page === totalPages}
          >
            {t("stock_overview_pagination_next")}
          </button>
        </div>
      </div>

      <RestockModal
        open={activeModal?.key === "restock"}
        onClose={closeActionModal}
        onCreated={closeAndRefetchStockData}
        venueId={org?.uid}
        product={activeModal?.row}
      />
      <InventoryModal
        open={activeModal?.key === "inventoryCount"}
        onClose={closeActionModal}
        onCreated={closeAndRefetchStockData}
        venueId={org?.uid}
        product={activeModal?.row}
      />
      <MovementsModal
        open={activeModal.key === "viewMovements"}
        onClose={closeActionModal}
        product={activeModal?.row}
        venueId={org?.uid}
        lossStats={lossStatsByItem[activeModal?.row?.id]}
      />
      <AssociatedProductsModal
        open={activeModal.key === "usedInProducts"}
        onClose={closeActionModal}
        product={activeModal?.row}
        orgProducts={products}
      />
      <CreateStockModal
        open={activeModal.key === "createStockProduct" || activeModal.key === "editStockProduct"}
        stockItem={activeModal?.row}
        categories={stockCategories}
        venueId={org?.uid}
        onCreated={closeAndRefetchStockData}
        onClose={closeActionModal}
      />
      <CreateStockCategoryModal
        open={activeModal.key === "createStockCategory" || activeModal.key === "editStockCategory"}
        venueId={org?.uid}
        category={activeModal?.row}
        onCreated={closeAndRefetchStockData}
        onClose={closeActionModal}
      />
      <ImportExportStockModal
        open={activeModal.key === "importExportStock"}
        onClose={closeActionModal}
        stockCategories={stockCategories}
        stockItems={stockItems}
        venueId={org?.uid}
        onImportSuccess={closeAndRefetchStockData}
      />
      <DeleteStockCategoryModal
        open={activeModal.key === "deleteStockCategory"}
        venueId={org?.uid}
        category={activeModal?.row}
        onDeleted={closeAndRefetchStockData}
        onClose={closeActionModal}
      />
      <DeleteStockItemsModal
        open={activeModal.key === "deleteStockItems" || activeModal.key === "deleteStockProduct"}
        onClose={closeActionModal}
        onDeleted={closeAndRefetchStockData}
        stockItemsIds={stockItemIdsToDelete}
        venueId={org?.uid}
      />
    </div>
  );
}

export default StockOverview;
